import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { adminUserSalesOrderAdminUserOrdersPage_user_salesOrders_items_SalesOrder as AdminSalesOrdersItemType } from '@Admin/AdminUserOrdersPage/__generated__/adminUserSalesOrderAdminUserOrdersPage'
import { userProfileQuery } from '@Admin/AdminUserProfilePage/query'
import { salesOrder_salesOrder as SalesOrderType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { ordersList_me_salesOrders_items_SalesOrder as SalesOrdersItemType } from '@Orders/AllOrdersPage/__generated__/ordersList'
import RemoveTeamQuoteOrderModal from '@Teams/RemoveTeamQuoteOrderModal'
import { TeamOrders_teamOrders_items_SalesOrder as TeamSalesOrder } from '@Teams/TeamDetailsPage/TeamOrdersPanel/__generated__/TeamOrders'

import {
    QuoteDetails_QuoteFragment as QuoteDetailsPageQuoteType,
    useUserTeamsQuery,
} from '~/gql/graphql'
import Button from '~/core/Button'
import Divider from '~/core/Divider'
import { getUserIdentity } from '~/redux-modules/identity'

const REMOVE_QUOTE = 'remove-'
const BUTTON_TYPE = 'button'

type SubShareOrDeleteQuotePropsType = Pick<
    QuoteDetailsPageQuoteType,
    'id' | 'name' | 'project' | 'user'
>

interface RemoveFromTeamProps {
    quote?: SubShareOrDeleteQuotePropsType
    order?:
        | TeamSalesOrder
        | SalesOrderType
        | SalesOrdersItemType
        | AdminSalesOrdersItemType
    teamId: number | undefined
    userId?: number
    ownerId?: number
    purchaserId?: number
    teamName?: string
    style?: { [key: string]: any }
    showDivider?: boolean
    type: 'button' | 'link'
    placement: string
}

export default function RemoveFromTeam(props: RemoveFromTeamProps) {
    const {
        quote,
        order,
        teamId,
        ownerId,
        purchaserId,
        teamName,
        placement,
        type,
        style,
        showDivider = true,
    } = props

    const [removeModalVisible, setRemoveModalVisible] = useState<boolean>(false)

    const identity = useSelector(getUserIdentity)
    const currentUserId = identity.id

    const { userId: userOboId = '' } = useParams<{
        userId: string
        teamId: string
    }>()

    const userId = +userOboId || currentUserId

    const { data } = useUserTeamsQuery({
        variables: { userId },
    })

    const { data: userData } = useQuery(userProfileQuery, {
        variables: {
            id: userId,
        },
        skip: !userId,
    })

    const isCollaborator = ownerId !== userId

    const isPurchaser = purchaserId === userId

    const userTeams = data?.user?.teams?.items || []

    const quoteInUsersTeam = userTeams.some(team => team.id === teamId)

    const isTeamsFeatureEnabled =
        userData?.user?.businessAccountMembership?.businessAccount
            ?.isTeamsFeatureEnabled

    const shouldNotShow =
        !isTeamsFeatureEnabled ||
        (isCollaborator && !quoteInUsersTeam && !isPurchaser) ||
        !teamId

    if (shouldNotShow) {
        return null
    }

    const buttonStyle =
        type === BUTTON_TYPE ? style : { padding: '4px 0px', ...style }

    return (
        <>
            {removeModalVisible && (
                <RemoveTeamQuoteOrderModal
                    quote={quote}
                    order={order}
                    teamId={teamId}
                    userId={userId}
                    teamName={teamName}
                    onClose={() => setRemoveModalVisible(false)}
                />
            )}
            <Button
                name={REMOVE_QUOTE}
                data-test-target={`${REMOVE_QUOTE}-button`}
                placement={placement}
                type={type === BUTTON_TYPE ? undefined : 'link'}
                onClick={() => setRemoveModalVisible(true)}
                style={buttonStyle}
            >
                Remove
            </Button>
            {showDivider && <Divider />}
        </>
    )
}
